import { PALETTE } from '../tokens';

// Inline sparkline. Two callers today:
//   1. SignalScore + Dashboard "what's next" panel — 68×26, single
//      trailing dot, color flipped by trend direction. The original
//      contract is preserved exactly via the ``data`` + ``up`` props.
//   2. Signal History cards (Dashboard dark card + Profile white card,
//      May 27) — variable size, no dot, color always antique gold.
//      Activated by passing ``width`` / ``height`` / ``showDot=false``.
//
// Renders nothing for <2 datapoints — callers can leave the call site
// unconditional and the component will silently no-op.
export function Sparkline({
  data,
  up,
  width = 68,
  height = 26,
  color,
  showDot = true,
  strokeWidth = 1.5,
}) {
  const clean = Array.isArray(data)
    ? data.filter(v => typeof v === "number" && Number.isFinite(v))
    : [];
  if (clean.length < 2) return null;

  const w = width;
  const h = height;
  const min = Math.min(...clean);
  const max = Math.max(...clean);
  const range = max - min || 1;

  const pts = clean.map((v, i) =>
    `${(i / (clean.length - 1)) * w},${h - ((v - min) / range) * h}`
  ).join(" ");
  const lastX = w;
  const lastY = h - ((clean[clean.length - 1] - min) / range) * h;
  // Backward-compat: legacy callers pass ``up`` and expect gold-vs-muted.
  // New callers pass an explicit ``color`` (always gold for the timeline
  // cards). Explicit color wins.
  const c = color || (up ? PALETTE.primary : PALETTE.textMute);

  return (
    <svg
      width={w}
      height={h}
      style={{ overflow: "visible", display: "block" }}
      role="img"
      aria-label="Recent signal trend"
    >
      <polyline
        points={pts}
        fill="none"
        stroke={c}
        strokeWidth={strokeWidth}
        strokeLinecap="round"
        strokeLinejoin="round"
        opacity={showDot ? 0.7 : 1}
      />
      {showDot && <circle cx={lastX} cy={lastY} r="3" fill={c} />}
    </svg>
  );
}
